import {
  InitScale, InitAxisCall, InitTip,
} from '../../helpers/init';
import MountBrush from '../../helpers/brush';
import {
  MountGrid, MountAxis, MountAxisLabelX, MountAxisLabelY, MountRef,
  MountClip, MountMainFrame, MountThresLine, MountBars, MountGraphLabel,
} from '../../helpers/mount';
import { TfRescale, MountCompass } from '../../helpers/compass';
import { PksEdit } from '../../helpers/converter';
import { LIST_LAYOUT } from '../../constants/list_layout';
import { LIST_ROOT_SVG_GRAPH, LIST_BRUSH_SVG_GRAPH } from '../../constants/list_graph';
import { resolveXExtent } from '../../helpers/resolve_extent';

const d3 = require('d3');

class RectFocus {
  constructor(props) {
    const {
      W, H, clickUiTargetAct, selectUiSweepAct, scrollUiWheelAct, graphIndex, uiSt,
    } = props;
    this.graphIndex = graphIndex;
    this.uiSt = uiSt;
    this.rootKlass = `.${LIST_ROOT_SVG_GRAPH.RECT}`;
    this.brushClass = `.${LIST_BRUSH_SVG_GRAPH.RECT}`;
    this.margin = {
      t: 5,
      b: 40,
      l: 60,
      r: 5,
    };
    this.w = W - this.margin.l - this.margin.r;
    this.h = H - this.margin.t - this.margin.b;
    this.clickUiTargetAct = clickUiTargetAct;
    this.selectUiSweepAct = selectUiSweepAct;
    this.scrollUiWheelAct = scrollUiWheelAct;
    this.brush = d3.brush();
    this.brushX = d3.brushX();

    this.axis = null;
    this.thresLine = null;
    this.grid = null;
    this.ref = null;
    this.ccPattern = null;
    this.data = [];
    this.dataPks = [];
    this.tTrEndPts = null;
    this.tSfPeaks = null;
    this.root = null;
    this.svg = null;
    this.bars = null;
    this.scales = InitScale(this, false);
    this.axisCall = InitAxisCall(5);
    this.pathCall = null;
    this.tip = null;
    this.factor = 0.125;
    this.currentExtent = null;
    this.layout = LIST_LAYOUT.MS;

    this.setTip = this.setTip.bind(this);
    this.setDataParams = this.setDataParams.bind(this);
    this.create = this.create.bind(this);
    this.update = this.update.bind(this);
    this.setConfig = this.setConfig.bind(this);
    this.drawBar = this.drawBar.bind(this);
    this.drawThres = this.drawThres.bind(this);
    this.drawGrid = this.drawGrid.bind(this);
    this.mergedPeaks = this.mergedPeaks.bind(this);
    this.isFirefox = typeof InstallTrigger !== 'undefined';
  }

  setTip() {
    this.tip = InitTip();
    this.root.call(this.tip);
  }

  setDataParams(data, peaks, tTrEndPts, tSfPeaks, layout = null) {
    this.data = [...data];
    this.dataPks = [...peaks];
    this.tTrEndPts = tTrEndPts;
    this.tSfPeaks = tSfPeaks;
    if (layout) {
      this.layout = layout;
    }
    this.xOnlyBrush = this.layout === LIST_LAYOUT.LC_MS;

    // setConfig only runs once this.data is non-empty, so this is the only
    // domain set when it's not — the same NaN-on-empty-data guard applies.
    const { xL, xU } = resolveXExtent(this.data, (d) => d.x);
    this.scales.x.domain([xL, xU]);
  }

  updatePathCall(xt, yt) {
    this.pathCall = d3.line()
      .x((d) => xt(d.x))
      .y((d) => yt(d.y));
  }

  setConfig(sweepExtentSt) {
    // Domain Calculate
    let { xExtent, yExtent } = sweepExtentSt || { xExtent: false, yExtent: false };

    if (!xExtent || !yExtent) {
      // resolveXExtent falls back to a fixed { xL: 0, xU: 1 } instead of NaN
      // when this.data is empty (or every x is undefined) — d3.extent alone
      // would return [undefined, undefined] here, and the padding/floor below
      // would turn that into NaN before it ever reached scales.x.domain().
      const { xL: fitXL, xU: fitXU } = resolveXExtent(this.data, (d) => d.x);
      const padding = (fitXU - fitXL) * 0.005;
      xExtent = { xL: Math.max(0, fitXL - padding), xU: fitXU + padding };
      const btm = 0; // MS baseline is always 0.
      const rawTop = d3.max(this.data, (d) => d.y);
      const top = Number.isFinite(rawTop) ? rawTop : 1; // same empty-data guard, for y
      const height = top - btm;
      yExtent = {
        yL: (btm - this.factor * height),
        yU: (top + this.factor * height),
      };
    }

    this.scales.x.domain([xExtent.xL, xExtent.xU]);
    this.scales.y.domain([yExtent.yL, yExtent.yU]);

    // rescale for zoom
    const { xt, yt } = TfRescale(this);

    // Axis Call
    this.axisCall.x.scale(xt);
    this.axisCall.y.scale(yt);

    this.currentExtent = { xExtent, yExtent };
  }

  posHeight(gnd, val) {
    const h = gnd - val;
    return h >= 0 ? h : 0;
  }

  barColor(y, yRef) {
    return y >= yRef ? 'steelblue' : '#aaa';
  }

  drawBar() {
    if (!this.bars) {
      return;
    }

    const { xt, yt } = TfRescale(this);
    this.updatePathCall(xt, yt);

    // tTrEndPts only decides barColor's above/below-threshold fill -- an empty list
    // (no threshold, no feature.thresRef) means there is nothing to compare against,
    // not that there is nothing to draw. -Infinity keeps every bar's default color
    // instead of skipping enter/exit/transform entirely, which left the chart blank
    // on mount or stale (un-removed, un-repositioned) on a later update/zoom.
    const yRef = this.tTrEndPts.length ? this.tTrEndPts[0].y : -Infinity;
    const bars = this.bars.selectAll('rect')
      .data(this.data);

    bars.exit()
      .attr('class', 'exit')
      .remove();

    const gnd = yt(0);
    bars.enter()
      .append('rect')
      .attr('class', 'enter-bar')
      .attr('width', 1.5)
      .merge(bars)
      .attr('fill', (d) => this.barColor(d.y, yRef))
      .attr('height', (d) => this.posHeight(gnd, yt(d.y)))
      .attr('id', (d) => `mpp${Math.round(1000 * d.x)}`)
      .attr('transform', (d) => `translate(${xt(d.x)}, ${yt(d.y)})`)
      .on('mouseover', (event, d) => {
        d3.select(`#mpp${Math.round(1000 * d.x)}`)
          .attr('stroke-opacity', '1.0');
        d3.select(`#bpt${Math.round(1000 * d.x)}`)
          .style('fill', 'blue');
        const tipParams = { d, layout: this.layout };
        this.tip.show(tipParams, event.target);
      })
      .on('mouseout', (event, d) => {
        d3.select(`#mpp${Math.round(1000 * d.x)}`)
          .attr('stroke-opacity', '1.0');
        d3.select(`#bpt${Math.round(1000 * d.x)}`)
          .style('fill', 'red');
        const tipParams = { d, layout: this.layout };
        this.tip.hide(tipParams, event.target);
      });
  }

  drawThres() {
    if (this.thresLine && this.tTrEndPts.length > 0) {
      this.thresLine.attr('d', this.pathCall(this.tTrEndPts));
      this.thresLine.attr('visibility', 'visible');
    } else if (this.thresLine) {
      this.thresLine.attr('visibility', 'hidden');
    }
  }

  drawGrid() {
    if (!this.grid) return;
    this.grid.x.call(this.axisCall.x
      .tickSize(-this.h, 0, 0))
      .selectAll('line')
      .attr('stroke', '#ddd')
      .attr('stroke-opacity', 0.6)
      .attr('fill', 'none');
    this.grid.y.call(this.axisCall.y
      .tickSize(-this.w, 0, 0))
      .selectAll('line')
      .attr('stroke', '#ddd')
      .attr('stroke-opacity', 0.6)
      .attr('fill', 'none');
  }

  mergedPeaks(editPeakSt) {
    if (!editPeakSt) return this.dataPks;
    this.dataPks = PksEdit(this.dataPks, editPeakSt);
    return this.dataPks;
  }

  create({
    filterSeed, filterPeak, tTrEndPts, tSfPeaks, layoutSt,
    sweepExtentSt, isUiAddIntgSt, isUiNoBrushSt,
  }) {
    this.svg = d3.select(this.brushClass);
    MountMainFrame(this, 'focus');
    MountClip(this);

    this.root = d3.select(this.rootKlass).selectAll('.focus-main');
    this.setTip();
    this.setDataParams(filterSeed, filterPeak, tTrEndPts, tSfPeaks, layoutSt);
    MountCompass(this);

    this.axis = MountAxis(this);
    [this.thresLine] = MountThresLine(this, 'green');
    this.grid = MountGrid(this);
    this.ref = MountRef(this);
    this.bars = MountBars(this);
    MountAxisLabelX(this);
    MountAxisLabelY(this);
    MountGraphLabel(this);

    if (this.data && this.data.length > 0) {
      this.setConfig(sweepExtentSt);
      this.drawBar();
      this.drawThres();
      this.drawGrid();
    }
    MountBrush(this, isUiAddIntgSt, isUiNoBrushSt, this.brushClass);
  }

  update({
    filterSeed, filterPeak, tTrEndPts, tSfPeaks, layoutSt,
    sweepExtentSt, isUiAddIntgSt, isUiNoBrushSt, uiSt,
  }) {
    this.root = d3.select(this.rootKlass).selectAll('.focus-main');
    this.setDataParams(filterSeed, filterPeak, tTrEndPts, tSfPeaks, layoutSt);
    this.uiSt = uiSt;

    if (this.data && this.data.length > 0) {
      this.setConfig(sweepExtentSt);
      this.drawBar();
      this.drawThres();
      this.drawGrid();
    }
    MountBrush(this, isUiAddIntgSt, isUiNoBrushSt, this.brushClass);
  }
}

export default RectFocus;
